import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import Footer from './Footer';
import InstallPrompt from './InstallPrompt';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import API_CONFIG from '../config/api';

const Layout = ({ children }) => {
  const { user } = useAuth();
  // The Log page is an app-like fixed screen that sizes itself to the viewport, so it
  // manages its own bottom clearance and drops the footer — otherwise the tall mobile
  // footer would hang below the fold and reintroduce the page scroll we're avoiding.
  const { pathname } = useLocation();
  const isAppView = pathname === '/log';
  const profilePictureUrl = user?.profile_picture
    ? `${API_CONFIG.UPLOADS_BASE_URL}/profile_pictures/${user.profile_picture}`
    : null;

  // Smooth scrolling for anchor links
  useEffect(() => {
    const handleSmoothScroll = (e) => {
      const target = e.target.closest('a');
      if (target && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const targetElement = document.querySelector(target.getAttribute('href'));
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  return (
    <>
      {/* Mobile Header — only visible on small screens. Brand wordmark (left,
          taps to Home) · profile (right). Each page carries its own heading in
          the body, so the header stays clean. Primary nav is the bottom bar. */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-surface-card dark:bg-surface-dark shadow-sm border-b border-surface-border dark:border-surface-dark-border">
        <div className="flex items-center justify-between px-4 py-2">
          <Link
            to="/home"
            aria-label="Vegetablah — Home"
            className="-ml-1 px-1 py-1 rounded-md text-lg font-extrabold tracking-tight text-gray-900 dark:text-white hover:opacity-80 transition-opacity"
          >
            Vegetablah
          </Link>

          {user ? (
            <Link
              to="/me"
              aria-label="Settings"
              className="w-9 h-9 rounded-full bg-solar-gradient flex items-center justify-center text-[#1a1200] text-sm font-semibold overflow-hidden shrink-0"
            >
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                user?.email?.charAt(0).toUpperCase()
              )}
            </Link>
          ) : (
            <div className="w-10" aria-hidden="true" />
          )}
        </div>
      </header>

      {/* Sidebar is the desktop primary nav (fixed left at md+). On mobile it
          stays off-screen — the bottom bar + header cover navigation there. */}
      <Sidebar isMobileMenuOpen={false} closeMobileMenu={() => {}} />

      <div className={`ml-0 md:ml-64 min-h-screen flex flex-col overflow-x-hidden bg-surface dark:bg-surface-dark transition-colors duration-300 pt-14 md:pt-0 ${isAppView ? 'pb-0' : 'pb-24 md:pb-0'}`}>
        <main className="flex-1">
          {children}
        </main>
        {!isAppView && <Footer />}
      </div>

      <BottomNav />

      <InstallPrompt />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  );
};

export default Layout;
